import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Historico.module.css';
import { listar, COLECOES } from '../../services/firestore';
import { db } from '../../firebase';
import {
  collection,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  getDocs,
} from 'firebase/firestore';
import { exportarExcel, formatarDataHora } from '../../utils/exportar';

// ====================================================================
// HISTÓRICO DE MOVIMENTAÇÃO — Firestore real
// Query server-side: orderBy(criadoEm, desc) + range de datas
// Filtros client-side: tipo, estoque, busca textual
// Paginação: 50 por vez com startAfter(lastDoc)
// ====================================================================

const LIMITE = 50;

// Mapeamento: campos UPPERCASE do Firestore → rótulos e classes CSS
const TIPO_CONFIG = {
  SAIDA:         { label: 'SAÍDA',         badge: 'hbSaida',     seta: '→' },
  ENTRADA:       { label: 'ENTRADA',        badge: 'hbEntrada',   seta: '→' },
  TRANSFERENCIA: { label: 'TRANSFERÊNCIA',  badge: 'hbTransf',    seta: '→' },
  DEVOLUCAO:     { label: 'DEVOLUÇÃO',      badge: 'hbDevolucao', seta: '↩' },
  ESTORNO:       { label: 'ESTORNO',        badge: 'hbEstorno',   seta: '▶' },
};

// cls = classe CSS existente; inlineStyle = override para diferenciar variantes
const STATUS_CONFIG = {
  EFETIVADA:   { label: 'Efetivada',   cls: 'stEfetivada', inlineStyle: null },
  EM_TRANSITO: { label: 'Em trânsito', cls: 'stEstornada', inlineStyle: { background: '#FFF8E6', color: '#7A5200', border: '1px solid #F0C060' } },
  NEGADA:      { label: 'Negada',      cls: 'stNegada',    inlineStyle: null },
  ESTORNADA:   { label: 'Estornada',   cls: 'stEstornada', inlineStyle: null },
  ESCALADA:    { label: '⚠ Escalada',  cls: 'stEstornada', inlineStyle: { background: '#FAE8D6', color: '#7A2800', border: '1px solid #C06030' } },
};

const COLUNAS_EXCEL = [
  { header: 'Data/Hora',   dataKey: 'dataHora' },
  { header: 'Tipo',        dataKey: 'tipo' },
  { header: 'Status',      dataKey: 'status' },
  { header: 'Origem',      dataKey: 'origem' },
  { header: 'Destino',     dataKey: 'destino' },
  { header: 'OS',          dataKey: 'os' },
  { header: 'NF',          dataKey: 'nf' },
  { header: 'Itens',       dataKey: 'itens' },
  { header: 'Total un',    dataKey: 'totalUn' },
  { header: 'Responsável', dataKey: 'responsavel' },
  { header: 'Aceito por',  dataKey: 'aceitoPor' },
  { header: 'Negado por',  dataKey: 'negadoPor' },
  { header: 'Motivo neg.', dataKey: 'motivoNegacao' },
  { header: 'Escalada em', dataKey: 'escaladaEm' },
];

// --- Helpers puros (fora do componente) ---

function dataParaInicioDia(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0);
}

function dataParaFimDia(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59);
}

function tsParaDisplay(ts) {
  if (!ts) return { data: '—', hora: '' };
  try {
    const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
    return {
      data: d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }),
      hora: d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
    };
  } catch {
    return { data: '—', hora: '' };
  }
}

function calcularRota(m) {
  switch (m.tipo) {
    case 'SAIDA':
      return { de: m.estoqueOrigemNome || '—', para: m.os || '(sem OS)' };
    case 'ENTRADA':
      return {
        de: m.nf ? `NF: ${m.nf}` : 'Compra',
        para: m.estoqueDestinoNome || '—',
      };
    case 'TRANSFERENCIA':
      return {
        de: m.estoqueOrigemNome || '—',
        para: m.estoqueDestinoNome || '—',
      };
    case 'DEVOLUCAO':
      return { de: m.os || '—', para: m.estoqueDestinoNome || '—' };
    case 'ESTORNO':
      return {
        de: m.estoqueOrigemNome || '—',
        para: m.estoqueDestinoNome || '—',
      };
    default:
      return { de: '—', para: '—' };
  }
}

function movParaLinhaExcel(m) {
  const rota = calcularRota(m);
  const tcfg = TIPO_CONFIG[m.tipo] || { label: m.tipo || '' };
  const scfg = STATUS_CONFIG[m.status] || { label: m.status || '' };
  const itensTexto = (m.itens || [])
    .map((it) => `${it.cor || it.nome || ''} × ${it.qtd}`)
    .join('; ');
  const totalUn =
    m.totalUnidades ??
    (m.itens || []).reduce((s, it) => s + (Number(it.qtd) || 0), 0);
  const negadoPorNome = m.negacaoNome || m.negadoPorNome || '';
  const negadoEm = m.negacaoEm ? ` · ${formatarDataHora(m.negacaoEm)}` : '';
  return {
    dataHora:      formatarDataHora(m.criadoEm),
    tipo:          tcfg.label,
    status:        scfg.label.replace('⚠ ', ''),
    origem:        rota.de,
    destino:       rota.para,
    os:            m.os || '',
    nf:            m.nf || '',
    itens:         itensTexto,
    totalUn,
    responsavel:   m.responsavelNome || m.responsavelEmail || '',
    aceitoPor:     m.aceiteNome
      ? `${m.aceiteNome}${m.aceiteEm ? ' · ' + formatarDataHora(m.aceiteEm) : ''}`
      : '',
    negadoPor:     negadoPorNome ? negadoPorNome + negadoEm : '',
    motivoNegacao: m.negacaoMotivo || m.motivoEstorno || '',
    escaladaEm:    m.escaladaEm ? formatarDataHora(m.escaladaEm) : '',
  };
}

async function buscarLote(dataInicio, dataFim, cursor) {
  const inicio = dataParaInicioDia(dataInicio);
  const fim = dataParaFimDia(dataFim);

  const ref = collection(db, 'movimentacoes');
  const clausulas = [orderBy('criadoEm', 'desc')];
  if (inicio) clausulas.push(where('criadoEm', '>=', inicio));
  if (fim) clausulas.push(where('criadoEm', '<=', fim));
  if (cursor) clausulas.push(startAfter(cursor));
  clausulas.push(limit(LIMITE + 1)); // +1 para detectar se há mais

  const snap = await getDocs(query(ref, ...clausulas));
  const docs = snap.docs;
  const temMaisLocal = docs.length > LIMITE;
  const docsBatch = temMaisLocal ? docs.slice(0, LIMITE) : docs;

  return {
    movs: docsBatch.map((d) => ({ id: d.id, ...d.data() })),
    temMais: temMaisLocal,
    ultimoDoc: docsBatch.length > 0 ? docsBatch[docsBatch.length - 1] : null,
  };
}

export default function Historico() {
  const navigate = useNavigate();

  const hoje = new Date().toISOString().slice(0, 10);
  const trintaDiasAtras = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

  // Dados
  const [movimentacoes, setMovimentacoes] = useState([]);
  const [ultimoDoc, setUltimoDoc] = useState(null);
  const [temMais, setTemMais] = useState(false);
  const [estoques, setEstoques] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const [erro, setErro] = useState('');

  // Filtros (data dispara nova query; tipo/estoque/busca são client-side)
  const [dataInicio, setDataInicio] = useState(trintaDiasAtras);
  const [dataFim, setDataFim] = useState(hoje);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroEstoque, setFiltroEstoque] = useState('');
  const [busca, setBusca] = useState('');

  // UI
  const [expandido, setExpandido] = useState(new Set());

  // Carrega estoques para o dropdown (uma vez)
  useEffect(() => {
    listar(COLECOES.ESTOQUES, { ordenarPor: 'nome' })
      .then((ests) => setEstoques(ests.filter((e) => e.ativo !== false)))
      .catch(() => {});
  }, []);

  // Carga inicial — re-executa quando o período de data muda
  const carregarInicial = useCallback(async () => {
    setCarregando(true);
    setErro('');
    setMovimentacoes([]);
    setUltimoDoc(null);
    setTemMais(false);
    setExpandido(new Set());
    try {
      const res = await buscarLote(dataInicio, dataFim, null);
      setMovimentacoes(res.movs);
      setTemMais(res.temMais);
      setUltimoDoc(res.ultimoDoc);
    } catch (e) {
      setErro(e.message || 'Erro ao carregar histórico.');
    } finally {
      setCarregando(false);
    }
  }, [dataInicio, dataFim]);

  useEffect(() => {
    carregarInicial();
  }, [carregarInicial]);

  // Paginação: acrescenta ao batch existente
  async function carregarMais() {
    if (carregandoMais || !ultimoDoc || !temMais) return;
    setCarregandoMais(true);
    setErro('');
    try {
      const res = await buscarLote(dataInicio, dataFim, ultimoDoc);
      setMovimentacoes((prev) => [...prev, ...res.movs]);
      setTemMais(res.temMais);
      setUltimoDoc(res.ultimoDoc);
    } catch (e) {
      setErro(e.message || 'Erro ao carregar mais registros.');
    } finally {
      setCarregandoMais(false);
    }
  }

  function toggle(id) {
    setExpandido((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });
  }

  // Filtros client-side sobre o batch carregado (não re-query)
  const movsFiltradas = useMemo(() => {
    return movimentacoes.filter((m) => {
      if (filtroTipo && m.tipo !== filtroTipo) return false;
      if (filtroEstoque) {
        const emOrigem = (m.estoqueOrigemNome || '') === filtroEstoque;
        const emDestino = (m.estoqueDestinoNome || '') === filtroEstoque;
        if (!emOrigem && !emDestino) return false;
      }
      if (busca) {
        const q = busca.toLowerCase();
        const itensTexto = (m.itens || [])
          .map((it) => `${it.cor || it.nome || ''} ${it.resina || ''}`)
          .join(' ');
        const match =
          itensTexto.toLowerCase().includes(q) ||
          (m.os || '').toLowerCase().includes(q) ||
          (m.responsavelNome || '').toLowerCase().includes(q) ||
          (m.estoqueOrigemNome || '').toLowerCase().includes(q) ||
          (m.estoqueDestinoNome || '').toLowerCase().includes(q);
        if (!match) return false;
      }
      return true;
    });
  }, [movimentacoes, filtroTipo, filtroEstoque, busca]);

  // Cards de resumo sobre o filtrado
  const resumo = useMemo(() => ({
    saida:     movsFiltradas.filter((m) => m.tipo === 'SAIDA').length,
    entrada:   movsFiltradas.filter((m) => m.tipo === 'ENTRADA').length,
    devolucao: movsFiltradas.filter((m) => m.tipo === 'DEVOLUCAO').length,
    transf:    movsFiltradas.filter((m) => m.tipo === 'TRANSFERENCIA').length,
    estorno:   movsFiltradas.filter((m) => m.tipo === 'ESTORNO').length,
  }), [movsFiltradas]);

  function handleExcel() {
    if (movsFiltradas.length === 0) return;
    exportarExcel({
      titulo: 'Histórico de Movimentação',
      colunas: COLUNAS_EXCEL,
      dados: movsFiltradas.map(movParaLinhaExcel),
      nomeAba: 'Histórico',
    });
  }

  return (
    <>
      {/* TOPBAR */}
      <div className={styles.topbar}>
        <div>
          <div className={styles.pageTitle}>Histórico de Movimentação</div>
          <div className={styles.pageSub}>
            Registro completo e imutável de todas as movimentações
          </div>
        </div>
        <div className={styles.topbarBtns}>
          <button
            className={styles.btnVoltar}
            onClick={() => navigate('/movimentacao')}
          >
            ← Voltar
          </button>
          <button
            className={styles.btnExport}
            onClick={handleExcel}
            disabled={movsFiltradas.length === 0}
            style={{ opacity: movsFiltradas.length === 0 ? 0.5 : 1, cursor: movsFiltradas.length === 0 ? 'not-allowed' : 'pointer' }}
          >
            ↓ Exportar Excel
          </button>
        </div>
      </div>

      {/* RESUMO */}
      <div className={styles.resumo}>
        <div className={`${styles.resCard} ${styles.saida}`}>
          <div className={styles.resNum}>{resumo.saida}</div>
          <div className={styles.resLabel}>Saídas</div>
        </div>
        <div className={`${styles.resCard} ${styles.entrada}`}>
          <div className={styles.resNum}>{resumo.entrada}</div>
          <div className={styles.resLabel}>Entradas</div>
        </div>
        <div className={`${styles.resCard} ${styles.devolucao}`}>
          <div className={styles.resNum}>{resumo.devolucao}</div>
          <div className={styles.resLabel}>Devoluções</div>
        </div>
        <div className={`${styles.resCard} ${styles.transf}`}>
          <div className={styles.resNum}>{resumo.transf}</div>
          <div className={styles.resLabel}>Transferências</div>
        </div>
        <div className={`${styles.resCard} ${styles.estorno}`}>
          <div className={styles.resNum}>{resumo.estorno}</div>
          <div className={styles.resLabel}>Estornos</div>
        </div>
      </div>

      {/* TOOLBAR — 5 colunas (inline override para acomodar os 2 campos de data) */}
      <div
        className={styles.toolbar}
        style={{ gridTemplateColumns: '1fr 150px 140px 130px 130px' }}
      >
        <input
          className={styles.toolbarInput}
          placeholder="🔍 Buscar produto, OS, responsável..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select
          className={styles.toolbarSelect}
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value)}
        >
          <option value="">Todos os tipos</option>
          {Object.entries(TIPO_CONFIG).map(([k, t]) => (
            <option key={k} value={k}>{t.label}</option>
          ))}
        </select>
        <select
          className={styles.toolbarSelect}
          value={filtroEstoque}
          onChange={(e) => setFiltroEstoque(e.target.value)}
        >
          <option value="">Todos estoques</option>
          {estoques.map((e) => (
            <option key={e.id} value={e.nome}>{e.nome}</option>
          ))}
        </select>
        <input
          className={styles.toolbarSelect}
          type="date"
          value={dataInicio}
          title="Data início"
          onChange={(e) => setDataInicio(e.target.value)}
        />
        <input
          className={styles.toolbarSelect}
          type="date"
          value={dataFim}
          title="Data fim"
          onChange={(e) => setDataFim(e.target.value)}
        />
      </div>

      {/* ERRO */}
      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '0 0 14px',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      {/* CABEÇALHO DA LISTA */}
      <div className={styles.histHead}>
        <span>
          <strong>{movsFiltradas.length}</strong> movimentações
          {temMais && ' (parcial — carregue mais abaixo)'}
          {' · '}mais recentes primeiro
          {carregando && ' · Carregando...'}
        </span>
        <span className={styles.hint}>Clique numa linha para ver detalhes</span>
      </div>

      {/* ESTADOS: carregando / vazio / lista */}
      {carregando ? (
        <div className={styles.semRes}>Carregando histórico...</div>
      ) : movsFiltradas.length === 0 ? (
        <div className={styles.semRes}>
          Nenhuma movimentação encontrada com os filtros aplicados.
        </div>
      ) : (
        <>
          <div className={styles.histList}>
            {movsFiltradas.map((m) => {
              const tcfg = TIPO_CONFIG[m.tipo] || {
                label: m.tipo || '?',
                badge: 'hbEstorno',
                seta: '→',
              };
              const scfg = STATUS_CONFIG[m.status] || {
                label: m.status || '?',
                cls: 'stEfetivada',
                inlineStyle: null,
              };
              const rota = calcularRota(m);
              const { data, hora } = tsParaDisplay(m.criadoEm);
              const aberto = expandido.has(m.id);
              const totalUn =
                m.totalUnidades ??
                (m.itens || []).reduce((s, it) => s + (Number(it.qtd) || 0), 0);

              return (
                <div
                  key={m.id}
                  className={`${styles.histRow} ${aberto ? styles.aberto : ''}`}
                >
                  {/* LINHA PRINCIPAL */}
                  <div className={styles.histMain} onClick={() => toggle(m.id)}>
                    <div className={styles.histData}>
                      <strong>{data}</strong>
                      {hora}
                    </div>
                    <div>
                      <span
                        className={`${styles.histTipoBadge} ${styles[tcfg.badge]}`}
                      >
                        {tcfg.label}
                      </span>
                      <div className={styles.histResumoTxt}>
                        {(m.itens || []).length}{' '}
                        {(m.itens || []).length === 1 ? 'produto' : 'produtos'}{' '}
                        · {totalUn} un
                        {m.os && <span> · {m.os}</span>}
                        {m.nf && <span> · {m.nf}</span>}
                      </div>
                    </div>
                    <div className={styles.histRota}>
                      <strong>{rota.de}</strong>{' '}
                      <span style={{ color: '#888' }}>{tcfg.seta}</span>{' '}
                      <strong>{rota.para}</strong>
                    </div>
                    <div className={styles.histResp}>
                      {m.responsavelNome || m.responsavelEmail || '—'}
                    </div>
                    <div>
                      <span
                        className={`${styles.histStatus} ${styles[scfg.cls]}`}
                        style={scfg.inlineStyle || undefined}
                      >
                        {scfg.label}
                      </span>
                    </div>
                    <div className={styles.histChevron}>▶</div>
                  </div>

                  {/* DETALHE EXPANDIDO */}
                  <div className={styles.histDetalhe}>
                    <div className={styles.detGrid}>
                      {/* Coluna esquerda: itens */}
                      <div className={styles.detBloco}>
                        <div className={styles.detTitulo}>
                          {m.status === 'NEGADA'
                            ? 'Itens (não efetivados)'
                            : m.tipo === 'ESTORNO'
                            ? 'Itens estornados'
                            : 'Itens movimentados'}
                        </div>
                        <div className={styles.detItens}>
                          {(m.itens || []).length === 0 ? (
                            <div className={styles.detItemRow}>
                              <span style={{ color: '#888', fontStyle: 'italic' }}>
                                Sem itens
                              </span>
                            </div>
                          ) : (
                            (m.itens || []).map((it, idx) => (
                              <div key={idx} className={styles.detItemRow}>
                                <span>
                                  {it.cor || it.nome || '—'}
                                </span>
                                <strong>
                                  {it.qtd} L
                                </strong>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {/* Coluna direita: informações */}
                      <div className={styles.detBloco}>
                        <div className={styles.detTitulo}>Informações</div>
                        {m.os && (
                          <div className={styles.detInfoRow}>
                            <span className={styles.lbl}>OS vinculada</span>
                            <span className={styles.val}>{m.os}</span>
                          </div>
                        )}
                        {m.nf && (
                          <div className={styles.detInfoRow}>
                            <span className={styles.lbl}>Nota Fiscal</span>
                            <span className={styles.val}>{m.nf}</span>
                          </div>
                        )}
                        {m.observacao && (
                          <div className={styles.detInfoRow}>
                            <span className={styles.lbl}>Observação</span>
                            <span className={styles.val}>{m.observacao}</span>
                          </div>
                        )}
                        {m.estoqueOrigemNome && (
                          <div className={styles.detInfoRow}>
                            <span className={styles.lbl}>Origem</span>
                            <span className={styles.val}>{m.estoqueOrigemNome}</span>
                          </div>
                        )}
                        {m.estoqueDestinoNome && (
                          <div className={styles.detInfoRow}>
                            <span className={styles.lbl}>Destino</span>
                            <span className={styles.val}>{m.estoqueDestinoNome}</span>
                          </div>
                        )}
                        {m.tipo === 'ESTORNO' && m.movimentacaoOrigemId && (
                          <div className={styles.detInfoRow}>
                            <span className={styles.lbl}>Ref. transferência</span>
                            <span
                              className={styles.val}
                              style={{ fontSize: 11, color: '#888' }}
                            >
                              {m.movimentacaoOrigemId}
                            </span>
                          </div>
                        )}
                        <div className={styles.detInfoRow}>
                          <span className={styles.lbl}>Responsável</span>
                          <span className={styles.val}>
                            {m.responsavelNome || m.responsavelEmail || '—'}
                          </span>
                        </div>
                        {m.aceiteNome && (
                          <div className={styles.detInfoRow}>
                            <span className={styles.lbl}>Aceito por</span>
                            <span className={styles.val}>
                              {m.aceiteNome}
                              {m.aceiteEm
                                ? ` · ${formatarDataHora(m.aceiteEm)}`
                                : ''}
                            </span>
                          </div>
                        )}
                        {(m.negacaoNome || m.negadoPorNome) && (
                          <div className={styles.detInfoRow}>
                            <span className={styles.lbl}>Negado por</span>
                            <span className={styles.val}>
                              {m.negacaoNome || m.negadoPorNome}
                              {m.negacaoEm
                                ? ` · ${formatarDataHora(m.negacaoEm)}`
                                : ''}
                            </span>
                          </div>
                        )}
                        {m.status === 'ESCALADA' && m.escaladaEm && (
                          <div className={styles.detInfoRow}>
                            <span className={styles.lbl}>Escalada em</span>
                            <span className={styles.val}>
                              {formatarDataHora(m.escaladaEm)}
                            </span>
                          </div>
                        )}
                        {m.status === 'ESCALADA' && m.escaladaMotivo && (
                          <div className={styles.detInfoRow}>
                            <span className={styles.lbl}>Motivo escalonamento</span>
                            <span className={styles.val}>{m.escaladaMotivo}</span>
                          </div>
                        )}
                        <div className={styles.detInfoRow}>
                          <span className={styles.lbl}>Data/hora</span>
                          <span className={styles.val}>
                            {formatarDataHora(m.criadoEm)}
                          </span>
                        </div>
                      </div>

                      {/* Motivo de negação / estorno — ocupa as 2 colunas */}
                      {(m.negacaoMotivo || m.motivoEstorno) && (
                        <div className={styles.detNegacao}>
                          <strong>Motivo da negação:</strong>
                          {m.negacaoMotivo || m.motivoEstorno}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* BOTÃO CARREGAR MAIS */}
          {temMais && (
            <div style={{ textAlign: 'center', padding: '20px 0 8px' }}>
              <button
                onClick={carregarMais}
                disabled={carregandoMais}
                style={{
                  background: '#fff',
                  border: '1px solid #D0D5DD',
                  borderRadius: 8,
                  padding: '10px 28px',
                  fontSize: 13,
                  color: '#4A5566',
                  cursor: carregandoMais ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  opacity: carregandoMais ? 0.6 : 1,
                  fontFamily: 'inherit',
                }}
              >
                {carregandoMais
                  ? 'Carregando...'
                  : `↓ Carregar mais ${LIMITE} registros`}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
